
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface Recipe {
  id: string;
  title: string;
  created_at: string;
  image_url: string | null;
  user_id: string;
  user: {
    username: string;
  };
}

interface Profile {
  id: string;
  username: string;
  avatar_url: string | null;
}

export const useDashboardData = () => {
  const navigate = useNavigate();
  const [favoriteRecipes, setFavoriteRecipes] = useState<Recipe[]>([]);
  const [subscriptions, setSubscriptions] = useState<Profile[]>([]);
  const [myRecipes, setMyRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/");
        return;
      }

      console.log("Fetching dashboard data for user:", session.user.id);

      const [favoritesResponse, subscriptionsResponse, myRecipesResponse] = await Promise.all([
        // Fetch favorite recipes
        supabase
          .from('favorites')
          .select(`
            recipe_id,
            recipes!favorites_recipe_id_fkey (
              id,
              title,
              user_id,
              created_at,
              image_url,
              profiles!recipes_user_id_fkey (
                username
              )
            )
          `)
          .eq('user_id', session.user.id),

        // Fetch subscriptions
        supabase
          .from('subscriptions')
          .select(`
            profiles!subscriptions_subscribed_to_id_fkey (
              id,
              username,
              avatar_url
            )
          `)
          .eq('subscriber_id', session.user.id),

        // Fetch my recipes
        supabase
          .from('recipes')
          .select(`
            id,
            title,
            created_at,
            image_url,
            user_id,
            profiles!recipes_user_id_fkey (
              username
            )
          `)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
      ]);

      if (favoritesResponse.error) throw favoritesResponse.error;
      if (subscriptionsResponse.error) throw subscriptionsResponse.error;
      if (myRecipesResponse.error) throw myRecipesResponse.error;

      console.log("Dashboard data received:", {
        favorites: favoritesResponse.data,
        subscriptions: subscriptionsResponse.data,
        myRecipes: myRecipesResponse.data
      });

      // Transform the data
      const favorites = favoritesResponse.data.map(f => ({
        ...f.recipes,
        user: { username: f.recipes.profiles.username }
      }));

      const subscriptionsList = subscriptionsResponse.data.map(s => s.profiles);
      
      const myRecipesList = myRecipesResponse.data.map(recipe => ({
        ...recipe,
        user: { username: recipe.profiles.username }
      }));

      setFavoriteRecipes(favorites);
      setSubscriptions(subscriptionsList);
      setMyRecipes(myRecipesList);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  return {
    favoriteRecipes,
    subscriptions,
    myRecipes,
    loading
  };
};
